import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

// ─── Animations ──────────────────────────────────────────────────────────────

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-30px) scale(0.95); }
  to   { opacity: 1; transform: translateX(0) scale(1); }
`;

const slideOutLeft = keyframes`
  from { opacity: 1; transform: translateX(0) scale(1); }
  to   { opacity: 0; transform: translateX(-30px) scale(0.95); }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4); }
  50%      { box-shadow: 0 0 0 6px rgba(34, 197, 94, 0); }
`;

const countPulse = keyframes`
  0%   { transform: scale(1); }
  50%  { transform: scale(1.08); }
  100% { transform: scale(1); }
`;

// ─── Styled Components ───────────────────────────────────────────────────────

const ToastWrapper = styled.div<{ $visible: boolean; $dismissed: boolean }>`
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 9998;
  max-width: 340px;
  width: calc(100vw - 48px);
  animation: ${({ $visible }) => $visible ? css`${slideInLeft} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards` : css`${slideOutLeft} 0.35s ease-in forwards`};
  display: ${({ $dismissed }) => $dismissed ? 'none' : 'block'};
  pointer-events: auto;

  @media (max-width: 480px) {
    bottom: 16px;
    left: 12px;
    max-width: calc(100vw - 24px);
  }
`;

const ToastCard = styled.div`
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08);
  border: 1px solid rgba(0,0,0,0.06);
  overflow: hidden;
  position: relative;
  backdrop-filter: blur(10px);
`;

const TopStrip = styled.div`
  height: 3px;
  background: linear-gradient(90deg, #22c55e, #16a34a, #15803d);
`;

const ToastBody = styled.div`
  padding: 10px 14px 8px;
`;

const CounterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const FireIcon = styled.span`
  font-size: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
`;

const CounterText = styled.div`
  flex: 1;
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const CounterLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CounterValue = styled.span<{ $animating: boolean }>`
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.5px;
  animation: ${({ $animating }) => $animating ? css`${countPulse} 0.4s ease` : 'none'};
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: rgba(0,0,0,0.06);
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.15s ease;
  font-size: 12px;
  line-height: 1;

  &:hover {
    background: rgba(0,0,0,0.1);
    color: #475569;
  }
`;

const ActivityRow = styled.div<{ $visible: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border-radius: 8px;
  border: 1px solid #bbf7d0;
  min-height: 28px;
  animation: ${({ $visible }) => $visible ? css`${fadeInUp} 0.4s ease forwards` : 'none'};
`;

const GreenDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
  animation: ${pulseGlow} 2s infinite;
`;

const ActivityText = styled.span`
  font-size: 12.5px;
  color: #15803d;
  font-weight: 500;
  line-height: 1.3;

  strong {
    font-weight: 700;
    color: #166534;
  }
`;

const UrgencyBadge = styled.div`
  margin-top: 5px;
  font-size: 10.5px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
`;

// ─── Inline Variant Styles ───────────────────────────────────────────────────

const InlineWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  animation: ${fadeInUp} 0.6s ease forwards;
`;

const InlineCounter = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  padding: 5px 12px;
  border-radius: 20px;
  border: 1px solid #fbbf24;
`;

const InlineCounterNum = styled.span<{ $animating: boolean }>`
  font-size: 15px;
  font-weight: 800;
  color: #92400e;
  font-variant-numeric: tabular-nums;
  animation: ${({ $animating }) => $animating ? css`${countPulse} 0.4s ease` : 'none'};
`;

const InlineCounterLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #92400e;
`;

const InlineActivity = styled.div<{ $visible: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border-radius: 20px;
  border: 1px solid #bbf7d0;
  animation: ${({ $visible }) => $visible ? css`${fadeInUp} 0.35s ease forwards` : 'none'};
`;

const InlineActivityText = styled.span`
  font-size: 12px;
  color: #15803d;
  font-weight: 600;
  white-space: nowrap;

  strong {
    font-weight: 700;
    color: #166534;
  }
`;

const InlineGreenDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
  animation: ${pulseGlow} 2s infinite;
`;

// ─── Hook: Phased Realistic Counter ──────────────────────────────────────────
// Counts from 0→target with progressively slower phases.
// Persists progress in sessionStorage so page refresh resumes seamlessly.
// Phase 1 (0–20%):  every 5–9 s
// Phase 2 (20–40%): every 30–49 s
// Phase 3 (40–60%): every 2–3 min
// Phase 4 (60%+):   every 7–9 min
// Cap: never exceeds target + 5

const STORAGE_KEY = 'socialProofCounter';

function getPhaseDelay(value: number, target: number): number {
  if (target === 0) return 5000;
  const pct = value / target;
  if (pct < 0.2)  return (5 + Math.random() * 4) * 1000;        // 5–9s
  if (pct < 0.4)  return (30 + Math.random() * 19) * 1000;      // 30–49s
  if (pct < 0.6)  return (120 + Math.random() * 60) * 1000;     // 2–3 min
  return (420 + Math.random() * 120) * 1000;                     // 7–9 min
}

function getPhaseAvgDelay(pct: number): number {
  if (pct < 0.2)  return 7000;
  if (pct < 0.4)  return 39500;
  if (pct < 0.6)  return 150000;
  return 480000;
}

function loadSavedCounter(target: number): number {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return 0;
    const saved = JSON.parse(raw);
    if (!saved || saved.target !== target) return 0;
    // Fast-forward: calculate how many ticks elapsed since last save
    const elapsed = Date.now() - (saved.timestamp || 0);
    if (elapsed <= 0) return Math.min(saved.value, target + 5);
    let val = saved.value;
    let remaining = elapsed;
    while (remaining > 0 && val < target + 5) {
      const pct = target > 0 ? val / target : 0;
      const avg = getPhaseAvgDelay(pct);
      if (remaining >= avg) {
        remaining -= avg;
        val += 1;
      } else {
        break;
      }
    }
    return Math.min(val, target + 5);
  } catch {
    return 0;
  }
}

function saveCounter(value: number, target: number) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ value, target, timestamp: Date.now() }));
  } catch { /* silent */ }
}

function useRealisticCounter(target: number): [number, boolean] {
  const [display, setDisplay] = useState(0);
  const [animating, setAnimating] = useState(false);
  const valueRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const mountedRef = useRef(true);
  const initializedRef = useRef(false);

  // On target load: restore saved progress or start from 0
  useEffect(() => {
    if (target === 0) return;
    if (initializedRef.current) return;
    initializedRef.current = true;
    const restored = loadSavedCounter(target);
    valueRef.current = restored;
    setDisplay(restored);
    saveCounter(restored, target);
  }, [target]);

  // Phased increment loop
  useEffect(() => {
    if (target === 0) return;
    mountedRef.current = true;
    const cap = target + 5;

    const scheduleNext = () => {
      if (!mountedRef.current) return;
      if (valueRef.current >= cap) return;
      const delay = getPhaseDelay(valueRef.current, target);
      timerRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        if (valueRef.current >= cap) return;
        const from = valueRef.current;
        valueRef.current += 1;
        saveCounter(valueRef.current, target);

        // Smooth +1 roll-up over 600ms
        setAnimating(true);
        const start = performance.now();
        const step = (now: number) => {
          if (!mountedRef.current) return;
          const progress = Math.min((now - start) / 600, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setDisplay(Math.round(from + eased));
          if (progress < 1) {
            requestAnimationFrame(step);
          } else {
            setAnimating(false);
            scheduleNext();
          }
        };
        requestAnimationFrame(step);
      }, delay);
    };

    // Start scheduling after a brief initial delay
    const initDelay = valueRef.current === 0 ? 1500 : 2000;
    timerRef.current = setTimeout(scheduleNext, initDelay);

    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [target]);

  return [display, animating];
}

// ─── Component ───────────────────────────────────────────────────────────────

interface SocialProofToastProps {
  variant?: 'toast' | 'inline';
}

const FALLBACK_NAMES = ['Raj***', 'Pra***', 'Ame***', 'San***', 'Vik***', 'Anu***', 'Moh***', 'Shr***'];

const SocialProofToast: React.FC<SocialProofToastProps> = ({ variant = 'toast' }) => {
  const [applicationCount, setApplicationCount] = useState(0);
  const [names, setNames] = useState<string[]>(FALLBACK_NAMES);
  const [currentNameIdx, setCurrentNameIdx] = useState(0);
  const [activityVisible, setActivityVisible] = useState(true);
  const [toastVisible, setToastVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  // Fetch social proof data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/public/social-proof`);
        const data = await res.json();
        if (data.success) {
          setApplicationCount(data.applicationCount || 150);
          if (data.recentNames?.length > 0) setNames(data.recentNames);
        }
      } catch {
        setApplicationCount(150);
      }
    };
    fetchData();
  }, []);

  // Rotate activity names every 4s
  const rotateActivity = useCallback(() => {
    setActivityVisible(false);
    setTimeout(() => {
      setCurrentNameIdx(prev => (prev + 1) % names.length);
      setActivityVisible(true);
    }, 400);
  }, [names.length]);

  useEffect(() => {
    intervalRef.current = setInterval(rotateActivity, 4500);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [rotateActivity]);

  // Delay toast appearance for home page (show after 2s)
  useEffect(() => {
    if (variant === 'toast') {
      const timer = setTimeout(() => setToastVisible(true), 2000);
      return () => clearTimeout(timer);
    } else {
      setToastVisible(true);
    }
  }, [variant]);

  const [displayCount, countAnimating] = useRealisticCounter(applicationCount);

  const handleDismiss = () => {
    setToastVisible(false);
    setTimeout(() => setDismissed(true), 400);
  };

  // Handle ESC key for toast variant
  useEffect(() => {
    if (variant !== 'toast') return;
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') handleDismiss(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  const currentName = names[currentNameIdx] || 'Someone';

  // ─── Inline Variant ────────────────────────────────────────────────────────
  if (variant === 'inline') {
    if (!toastVisible) return null;
    return (
      <InlineWrapper>
        <InlineCounter>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <rect x="4" y="2" width="16" height="20" rx="2" />
            <line x1="12" y1="10" x2="12" y2="16" />
            <rect x="8" y="5" width="8" height="3" rx="1" fill="#fde68a" stroke="none" />
            <line x1="10" y1="2" x2="10" y2="0" />
            <line x1="14" y1="2" x2="14" y2="0" />
          </svg>
          <InlineCounterNum $animating={countAnimating}>{displayCount}</InlineCounterNum>
          <InlineCounterLabel>applications</InlineCounterLabel>
        </InlineCounter>
        <InlineActivity $visible={activityVisible}>
          <InlineGreenDot />
          <InlineActivityText>
            <strong>{currentName}</strong> is filling the form
          </InlineActivityText>
        </InlineActivity>
      </InlineWrapper>
    );
  }

  // ─── Toast Variant (bottom-left floating) ──────────────────────────────────
  if (dismissed || !toastVisible) return null;

  return (
    <ToastWrapper $visible={toastVisible} $dismissed={dismissed}>
      <ToastCard>
        <TopStrip />
        <CloseBtn onClick={handleDismiss} title="Dismiss">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </CloseBtn>
        <ToastBody>
          <CounterRow>
            <FireIcon>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="2" width="16" height="20" rx="2" />
                <line x1="12" y1="10" x2="12" y2="16" />
                <circle cx="12" cy="18" r="0.5" fill="#16a34a" />
                <rect x="8" y="5" width="8" height="3" rx="1" fill="#bbf7d0" stroke="none" />
                <line x1="10" y1="2" x2="10" y2="0" />
                <line x1="14" y1="2" x2="14" y2="0" />
              </svg>
            </FireIcon>
            <CounterText>
              <CounterLabel>Applications Received</CounterLabel>
              <CounterValue $animating={countAnimating}>{displayCount}</CounterValue>
            </CounterText>
          </CounterRow>
          <ActivityRow $visible={activityVisible}>
            <GreenDot />
            <ActivityText>
              <strong>{currentName}</strong> is filling the CNG Dealership Form
            </ActivityText>
          </ActivityRow>
          <UrgencyBadge>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
            </svg>
            Don't miss out — application deadline 31st March 2026
          </UrgencyBadge>
        </ToastBody>
      </ToastCard>
    </ToastWrapper>
  );
};

export default SocialProofToast;
